import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Sandwich, Croissant, BookOpen, Bike } from "lucide-react";
import styled, { css, keyframes } from "styled-components";
import en from "../../language/Eng_Aust.json";
import { isMobileDevice, openUberEats } from "./uberEatsLink";

const Topmenu: React.FC = () => {
  const { pathname } = useLocation();
  const playIntro = pathname === "/";
  const handleUberEatsClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isMobileDevice()) return;

    event.preventDefault();
    openUberEats();
  };

  return (
    <>
      <Header>
        <Logo to="/" aria-label={en.home.navigation.logoAriaLabel}>
          <img src="/assets/Buttered/Buttered_logo/Top_Menu_logo.webp" alt={en.home.navigation.logoAlt} />
        </Logo>
      </Header>
      <BottomNav $playIntro={playIntro} aria-label={en.home.navigation.ariaLabel}>
        <NavLink to="/" end><span><Sandwich size={22} /></span>{en.home.navigation.home}</NavLink>
        <NavLink to="/mainmenu"><span><Croissant size={22} /></span>{en.home.navigation.menu}</NavLink>
        <NavLink to="/about"><span><BookOpen size={22} /></span>{en.home.navigation.story}</NavLink>
        <a className="order" href={en.home.links.uberEats} target="_blank" rel="noopener noreferrer" aria-label={en.home.navigation.uberEatsAriaLabel} onClick={handleUberEatsClick}>
          <span><Bike size={22} /></span>{en.home.navigation.uberEats}
        </a>
      </BottomNav>
    </>
  );
};
export default Topmenu;

const navigationReveal = keyframes`
  0% {
    clip-path: inset(0 calc(50% - 28px) 0 calc(50% - 28px) round 999px);
    opacity: 0;
  }
  16% {
    clip-path: inset(0 calc(50% - 28px) 0 calc(50% - 28px) round 999px);
    opacity: 1;
  }
  35% {
    clip-path: inset(0 calc(50% - 28px) 0 calc(50% - 28px) round 999px);
    opacity: 1;
  }
  100% {
    clip-path: inset(0 0 0 0 round 999px);
    opacity: 1;
  }
`;

const navigationItemPop = keyframes`
  0% { opacity: 0; transform: translateY(5px) scale(.55); }
  65% { opacity: 1; transform: translateY(-1px) scale(1.06); }
  84% { transform: translateY(0) scale(.985); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const Header = styled.header`
  width: 90%; max-width: 1200px; margin: auto; height: 104px;
  display: flex; align-items: center;
  @media(max-width: 768px) { width: calc(100% - 40px); height: 80px; }
`;
const Logo = styled(NavLink)`
  display: flex; align-items: center;
  img { height: 76px; width: auto; }
  @media(max-width: 768px) { img { height: 58px; } }
`;
const BottomNav = styled.nav<{ $playIntro: boolean }>`
  display: flex; position: fixed; z-index: 1000; top: 16px;
  left: 50%; transform: translateX(-50%); width: min(440px, calc(100% - 240px));
  box-sizing: border-box; padding: 7px;
  background: rgba(255,253,249,.98); border: 1px solid #eee7dd;
  border-radius: 999px; box-shadow: 0 10px 36px rgba(56,46,35,.12);
  backdrop-filter: blur(14px);
  ${({ $playIntro }) => $playIntro && css`
    animation: ${navigationReveal} 700ms cubic-bezier(.2, .8, .25, 1) backwards;

    a {
      animation: ${navigationItemPop} 560ms cubic-bezier(.16, 1, .3, 1) backwards;
    }
    a:nth-child(2), a:nth-child(3) { animation-delay: 700ms; }
    a:nth-child(1), a:nth-child(4) { animation-delay: 810ms; }
  `}
  a {
    flex: 1; min-width: 0; display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 2px; color: #80776c;
    text-decoration: none; font-size: 11px; font-weight: 650; min-height: 54px;
    border-radius: 999px; transition: background .2s, color .2s, transform .2s;
  }
  span {
    display: flex; align-items: center; justify-content: center;
    width: 46px; height: 32px; border-radius: 18px; transition: background .2s;
  }
  svg { stroke-width: 1.8; }
  a.active, a:hover { color: #342e28; background: #fff1a6; }
  a:hover { transform: translateY(-1px); }
  a.active span, a:hover span { background: transparent; }
  a.order { color: #28543a; }
  a.order { background: #e9f2e5; }
  a.order span { background: transparent; }
  a.order:hover { background: #d7e9d0; }
  @media (max-width: 768px) {
    top: auto; bottom: 0; left: 0; transform: none; width: 100%;
    padding: 8px 8px calc(8px + env(safe-area-inset-bottom));
    border-radius: 22px 22px 0 0; border-bottom: 0;
    a { border-radius: 18px; }
    a.active, a:hover { background: transparent; transform: none; }
    a.active span, a:hover span { background: #fff1a6; }
    a.order { background: transparent; }
    a.order span { background: #e9f2e5; }
  }

`;
